import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { fileUrl } from '@/lib/api';

/**
 * Fullscreen image viewer. Click anywhere outside the image (or X / Esc) to close.
 * Props: src (string|null) — when null/empty, nothing is rendered.
 *        alt (string)
 *        onClose() => void
 */
export default function ImageLightbox({ src, alt = '', onClose }) {
  useEffect(() => {
    if (!src) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose?.(); } };
    document.addEventListener('keydown', onKey, true);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = '';
    };
  }, [src, onClose]);

  if (!src) return null;
  const url = fileUrl(src);

  // Stop pointer events from bubbling to Radix Dialog's outside-click handler
  // (Radix listens on the document for pointerdown/click to close on outside click).
  const trap = (e) => { e.stopPropagation(); };
  const trapAll = (e) => { e.stopPropagation(); e.nativeEvent?.stopImmediatePropagation?.(); };

  // Render via portal directly on <body> so we escape any parent stacking context
  // (e.g. shadcn Sheet creates one via transforms and would otherwise sit above us).
  // Force pointer-events: auto because Radix Dialog sets pointer-events: none on body
  // siblings when a modal Sheet is open, which would otherwise block our buttons.
  return createPortal(
    <div
      data-testid="image-lightbox"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-md grid place-items-center"
      style={{ pointerEvents: 'auto' }}
      onPointerDown={trapAll}
      onMouseDown={trapAll}
      onClick={(e) => { trap(e); onClose?.(); }}
    >
      <button
        type="button"
        data-testid="lightbox-close"
        onPointerDown={trapAll}
        onMouseDown={trapAll}
        onClick={(e) => { e.preventDefault(); trapAll(e); onClose?.(); }}
        style={{ pointerEvents: 'auto' }}
        className="absolute top-4 right-4 w-12 h-12 grid place-items-center rounded-full bg-white text-black hover:bg-[#FF007F] hover:text-white transition shadow-2xl z-10"
        aria-label="Fermer"
      >
        <X className="w-6 h-6" />
      </button>
      <img
        src={url}
        alt={alt}
        onPointerDown={trapAll}
        onMouseDown={trapAll}
        onClick={trapAll}
        className="max-w-[95vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
      />
    </div>,
    document.body,
  );
}
